import { useEffect } from "react";

const GA_ID =
  (import.meta.env as { VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID?: string })
    .VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID ?? "G-WSEFL4M44Y";

/**
 * Loads Google Analytics after the page is interactive (idle or first user
 * interaction) so it never competes with the initial render.
 */
export function DeferredAnalytics() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (document.getElementById("ga-script")) return;

    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      cleanup();

      const s = document.createElement("script");
      s.id = "ga-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);

      const w = window as unknown as {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
      };
      w.dataLayer = w.dataLayer || [];
      w.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer.push(arguments);
      };
      w.gtag("js", new Date());
      w.gtag("config", GA_ID, {
        // Keep standard GA first-party cookies (_ga) but block Google's
        // advertising trackers that set third-party cookies (DoubleClick).
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
    };

    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, load));
      if (timer) window.clearTimeout(timer);
    };

    events.forEach((e) =>
      window.addEventListener(e, load, { once: true, passive: true }),
    );

    let timer: number | undefined;
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
    }).requestIdleCallback;
    if (ric) ric(load, { timeout: 5000 });
    else timer = window.setTimeout(load, 4000);

    return cleanup;
  }, []);

  return null;
}
